import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const useCurtNetworkStats = () => {
  return useQuery({
    queryKey: ['curt-network-stats'],
    queryFn: async () => {
      // Get total participants in CURT channel
      const { data: curtUsers } = await supabase
        .from('user_organizations')
        .select('user_id')
        .eq('channels.slug', 'curt');

      const totalParticipants = new Set(curtUsers?.map(u => u.user_id) || []).size;

      // Get active contributors in CURT channel
      const { data: kbCreators } = await supabase
        .from('knowledge_bases')
        .select('creator_id')
        .eq('channel_name', 'CURT')
        .eq('status', 'published');

      const { data: solutionCreators } = await supabase
        .from('solutions')
        .select('creator_id')
        .eq('channel_name', 'CURT')
        .eq('status', 'published');

      const { data: callCreators } = await supabase
        .from('calls')
        .select('creator_id')
        .eq('channel_name', 'CURT');

      const { data: sprintCreators } = await supabase
        .from('prompt_architecture_sprints')
        .select('creator_id')
        .eq('channel_name', 'CURT');

      const { data: inviterIds } = await supabase
        .from('invitations')
        .select('inviter_id')
        .eq('channel_name', 'CURT');

      // Combine and deduplicate creator IDs
      const allCreatorIds = new Set([
        ...(kbCreators?.map(k => k.creator_id) || []),
        ...(solutionCreators?.map(s => s.creator_id) || []),
        ...(callCreators?.map(c => c.creator_id) || []),
        ...(sprintCreators?.map(s => s.creator_id) || []),
        ...(inviterIds?.map(i => i.inviter_id) || [])
      ]);

      const activeContributors = allCreatorIds.size;

      // Get published knowledge bases count in CURT
      const { count: knowledgeBases } = await supabase
        .from('knowledge_bases')
        .select('*', { count: 'exact', head: true })
        .eq('channel_name', 'CURT')
        .eq('status', 'published')
        .is('deleted_at', null);

      // CURT has 22 hypothetical demo marketplace assets
      const totalSolutions = 22;

      // Get department distribution for CURT users
      const { data: curtUserIds } = await supabase
        .from('user_organizations')
        .select('user_id')
        .eq('channels.slug', 'curt');

      const curtUserIdSet = new Set(curtUserIds?.map(u => u.user_id) || []);

      const { data: expertiseData } = await supabase
        .from('user_expertise')
        .select('domain_name, user_id')
        .in('user_id', Array.from(curtUserIdSet));

      // Count unique users per domain
      const departmentMap: Record<string, Set<string>> = {};
      expertiseData?.forEach(exp => {
        if (!departmentMap[exp.domain_name]) {
          departmentMap[exp.domain_name] = new Set();
        }
        departmentMap[exp.domain_name].add(exp.user_id);
      });

      // Convert to array and take top 5
      const departments = Object.entries(departmentMap)
        .map(([name, userIds]) => ({
          name,
          participants: userIds.size
        }))
        .sort((a, b) => b.participants - a.participants)
        .slice(0, 5);

      // If no expertise data, return empty departments
      const finalDepartments = departments.length > 0 ? departments : [
        { name: "No data yet", participants: 0 }
      ];

      return {
        totalParticipants,
        activeContributors,
        knowledgeBases: knowledgeBases || 0,
        totalSolutions: totalSolutions || 0,
        departments: finalDepartments
      };
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  });
};
